import register from 'preact-custom-element'
import useMessage from 'hooks/useMessage'
import { useEffect, useState, useRef } from 'preact/hooks'
import initValidate from 'libs/validate'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { BIS_ENDPOINT } from 'helpers/constants'
import CustomModalDialog from 'snippets/custom-modal-dialog/custom-modal-dialog'
import ProductBackInStockContent from 'snippets/product/product-back-in-stock/product-back-in-stock-content'

const selectors = {
  MODAL_PRODUCT_BIS_SELECTOR: 'modal-product-bis'
}

function ProductBackInStock() {
  const [currentVariant, setCurrentVariant] = useState(null)
  const [product, setProduct] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const formBIS = useRef()
  const { message, initMessage, clearMessage } = useMessage()
  const TENANT_ID = GM_STATE.shopify.shop

  useEffect(() => {
    const handleOpenModal = (value) => {
      setProduct(value.product)
      setCurrentVariant(value.currentVariant)

      formBIS.current?.reset()
      clearMessage()
    }

    globalEmitter.on(emitterProps.product.openModalBIS, handleOpenModal)

    return () => {
      globalEmitter.off(emitterProps.product.openModalBIS, handleOpenModal)
    }
  }, [])

  const getAccessToken = async () => {
    const TOKEN_URL = `${BIS_ENDPOINT.GET_ACCESS_TOKEN}?tenant_id=${TENANT_ID}`

    try {
      const response = await fetch(TOKEN_URL, {
        method: 'GET'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      const data = await response.json()

      return data.access_token || null
    } catch (error) {
      console.error('Error fetching access token:', error)

      return null
    }
  }

  const handleSubmit = async (form, variant) => {
    const dataForm = new FormData(form.current)
    const email = dataForm.get('email')
    const marketingChecked = !!dataForm.get('marketing')

    if (!email || !variant?.id) {
      console.warn('Email or variantId not found')

      return
    }

    const formData = {
      email,
      subscribe: marketingChecked,
      variantRef: variant.id,
      notifyMe: true
    }

    try {
      setIsLoading(true)

      const ACCESS_TOKEN = await getAccessToken()

      if (!ACCESS_TOKEN) {
        console.error('Access token not found')

        return
      }

      const response = await fetch(BIS_ENDPOINT.SUBMIT_FORM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${ACCESS_TOKEN}`,
          'X-Twc-Tenant': TENANT_ID
        },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const errorText = await response.text()

        initMessage('', translate.product.notifyMeMessageError, 'error', 5000)
        throw new Error(`Server Error: ${JSON.parse(errorText).message}`)
      }

      initMessage('', translate.product.notifyMeMessageSuccess, 'success', 5000)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching access token:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!formBIS.current) return

    const validationInstance = initValidate(formBIS.current, true).onSuccess(() =>
      handleSubmit(formBIS, currentVariant)
    )

    // eslint-disable-next-line consistent-return
    return () => validationInstance?.destroy?.()
  }, [currentVariant])

  return (
    <CustomModalDialog
      id={selectors.MODAL_PRODUCT_BIS_SELECTOR}
      header={translate.product.notifyMeWhenAvailable}
      content={
        <ProductBackInStockContent formBIS={formBIS} product={product} isLoading={isLoading} message={message} />
      }
      classClose="!p-0 !outline-none"
      classHeader="p-5 uppercase text-xs text-neutral-900 sticky top-0 bg-white z-[60] font-extrabold border-b border-neutral-900"
      classMain="w-[calc(100%-48px)] max-w-[456px] h-full overflow-y-auto overflow-x-hidden"
      type="slideout"
      scrollTarget=".modal-dialog-content"
    />
  )
}

if (!customElements.get('product-back-in-stock')) {
  register(ProductBackInStock, 'product-back-in-stock')
}
